import { LingInputTypes } from './ling-input-types';
import { AbstractControl, FormControl } from '@angular/forms';
import { numDigits } from '../../../core/utils';
import { TranslateService } from '@ngx-translate/core';
import { LingRegExps } from '../../../core/data/ling-reg-exps';

export function processValueOnInputEvent(
  control: FormControl,
  type: LingInputTypes,
  onChangeFn: (value: any) => void,
  event: any,
  params?: {
    counterLimit?: number;
    counterHidePadding?: boolean;
  }
) {
  const originalRawValue =
    event.target.getAttribute('data-model-raw-value') || '';
  const newRawValue = event.target.value.replace(
    getFormatted(
      type,
      originalRawValue.substring(0, event.target.value.length),
      {
        counterLimit: params?.counterLimit,
        event,
        counterHidePadding: params?.counterHidePadding,
      }
    ),
    originalRawValue.substring(0, event.target.value.length)
  );
  event.target.setAttribute('data-model-raw-value', newRawValue);
  event.target.value = getFormatted(type, newRawValue, {
    counterLimit: params?.counterLimit,
    event,
    counterHidePadding: params?.counterHidePadding,
  });
  onChangeFn(getProcessed(type, newRawValue, params));
}

export function getFormatted(
  type: LingInputTypes,
  value: any,
  params?: {
    counterLimit?: number;
    event?: any;
    counterHidePadding?: boolean;
    currency?: string;
  }
): string {
  switch (type) {
    case 'number': {
      if (!value && value !== 0) {
        return value;
      }
      value = value.replace(/\D/g, '');
      while (value.charAt(0) === '0' && value.length > 1) {
        value = value.slice(1);
      }
      return value;
    }
    case 'counter': {
      if (!value && value !== 0) {
        return value;
      }

      if (
        String(value).at(0) === '0' &&
        params?.event?.inputType === 'deleteContentBackward'
      ) {
        while (value.charAt(0) === '0') {
          value = value.slice(1);
        }
        if (!value) {
          return '';
        }
      }

      if (typeof value === 'string') {
        value = value.replace(/\D/g, '');
        while (value.charAt(0) === '0' && value.length > 1) {
          value = value.slice(1);
        }
      } else {
        value = String(value);
      }

      if (!value) {
        return '';
      }
      if (params?.counterLimit) {
        const valueAsNumber = Number(value);
        if (valueAsNumber > params.counterLimit) {
          value = String(params.counterLimit);
        }
        if (!params.counterHidePadding) {
          value = value.padStart(numDigits(params.counterLimit), '0');
        }
      }

      return value;
    }
    case 'color': {
      if (!value) {
        return value;
      }

      if (typeof value === 'string') {
        let result = value.replace(/[^A-Fa-f0-9]/g, '');

        if (!result.startsWith('#')) {
          result = `#${result}`;
        }

        if (result.length > 7) {
          result = result.substring(0, 7);
        }
        return result;
      }

      return value;
    }
    default: {
      if (typeof value === 'string') {
        const split = value.split(' ');
        if (split.length >= 2 && split.at(-1) === '' && split.at(-2) === '') {
          return value.trim();
        }
        return value;
      } else {
        return String(Math.abs(value));
      }
    }
  }
}

export function getProcessed(
  type: LingInputTypes,
  value: any,
  params?: { counterLimit?: number; event?: any; counterHidePadding?: boolean }
): string | number | undefined | null {
  switch (type) {
    case 'number': {
      if (!value && value !== 0) {
        return value;
      }
      let result;
      if (typeof value === 'number') {
        result = Math.abs(value);
      }
      if (typeof value === 'string') {
        const filteredString = value.replace(/\D/g, '');
        result = filteredString.length ? Number(filteredString) : null;
      }
      return result;
    }
    case 'counter': {
      if (!value && value !== 0) {
        return value;
      }
      let result;
      if (typeof value === 'number') {
        result = Math.abs(value);
      }
      if (typeof value === 'string') {
        const filteredString = value.replace(/\D/g, '');
        result = filteredString.length ? Number(filteredString) : undefined;
      }

      if (result && params?.counterLimit && result > params.counterLimit) {
        result = params.counterLimit;
      }
      return result;
    }
    case 'color': {
      if (!value) {
        return value;
      }

      if (typeof value === 'string') {
        let result = value.replace(/[^A-Fa-f0-9]/g, '');

        if (!result.startsWith('#')) {
          result = `#${result}`;
        }

        if (result.length > 7) {
          result = result.substring(0, 7);
        }
        return result;
      }

      return value;
    }
    default: {
      if (typeof value === 'string') {
        const split = value.split(' ');
        if (split.length >= 2 && split.at(-1) === '' && split.at(-2) === '') {
          return value.trim();
        }
        return value;
      } else {
        return Math.abs(value);
      }
    }
  }
}

function getPatternKey(expectedPattern: LingRegExps) {
  return Object.keys(LingRegExps)[
    Object.values(LingRegExps)
      .map((value) =>
        typeof value === 'object' ? (value as RegExp).toString() : value
      )
      .indexOf(expectedPattern)
  ];
}

export function getFirstValidationError(
  control: AbstractControl,
  translateService: TranslateService
): string {
  let errorText = '';
  if (control.errors) {
    const errors = control.errors;
    for (const key of Object.keys(errors)) {
      switch (key) {
        case 'required': {
          errorText = translateService.instant('inputError.required');
          break;
        }
        case 'min': {
          errorText = translateService.instant('inputError.minAmount', {
            value: errors['min'].min,
          });
          break;
        }
        case 'max': {
          errorText = translateService.instant('inputError.maxAmount', {
            value: errors['max'].max,
          });
          break;
        }
        case 'minlength': {
          errorText = translateService.instant('inputError.minCharacters', {
            value: errors['minlength'].requiredLength,
          });
          break;
        }
        case 'maxlength': {
          errorText = translateService.instant('inputError.maxCharacters', {
            value: errors['maxlength'].requiredLength,
          });
          break;
        }
        case 'minNumberLength': {
          errorText = translateService.instant('inputError.minCharacters', {
            value: errors['minNumberLength'].requiredLength,
          });
          break;
        }
        case 'maxNumberLength': {
          errorText = translateService.instant('inputError.maxCharacters', {
            value: errors['maxNumberLength'].requiredLength,
          });
          break;
        }
        case 'minArrayLength': {
          errorText = translateService.instant(
            errors['minArrayLength'].requiredLength === 1
              ? 'inputError.minItemsSingle'
              : 'inputError.minItemsMultiple',
            {
              value: errors['minArrayLength'].requiredLength,
            }
          );
          break;
        }
        case 'maxArrayLength': {
          errorText = translateService.instant(
            errors['maxArrayLength'].requiredLength === 1
              ? 'inputError.maxItemsSingle'
              : 'inputError.maxItemsMultiple',
            {
              value: errors['maxArrayLength'].requiredLength,
            }
          );
          break;
        }
        case 'pattern': {
          errorText = translateService.instant(
            `inputError.valid.${getPatternKey(
              errors['pattern'].requiredPattern
            )}`
          );
          break;
        }
        case 'sameAs': {
          errorText = translateService.instant('inputError.sameAs', {
            inputName: errors['sameAs'].targetControlName,
          });
          break;
        }
        case 'compareTo': {
          errorText = errors['compareTo'].errorMessage;
          break;
        }
        default:
          errorText = translateService.instant('inputError.default');
      }
      if (errorText) {
        break;
      }
    }
  }
  return errorText;
}

export function triggerInputEvent(
  type: string,
  htmlInputElement: HTMLInputElement
) {
  let event = new Event(type, { bubbles: true });
  htmlInputElement['dispatchEvent'].apply(htmlInputElement, [event]);
}
